import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useUrlSearchParams } from 'use-url-search-params';
import { Table, Row, Col, Spinner, Button, Input } from 'reactstrap';
import { DateRangePicker } from 'rsuite';
import moment from 'moment';
import DHPagination from '../../../../components/Pagination';
import { fetchServiceFeedbackList } from '../actions/action';
import ServiceFeedbackDetailsOffcanvas from './ServiceFeedbackDetailsOffcanvas';

const SERVICE_TYPE_OPTIONS = [
  'filter_change',
  'water_leakage',
  'installation',
  'reinstallation',
  'amc',
  'others',
];

const getRatingColor = (rating) => {
  const value = Number(rating);
  if (value < 3) return '#dc3545';
  if (value === 3) return '#f5b301';
  return '#28a745';
};

const renderStars = (rating) => {
  const value = Number(rating) || 0;
  const color = getRatingColor(value);
  return (
    <span className="d-inline-flex align-items-center" title={`${value} / 5`}>
      {[1, 2, 3, 4, 5].map((star) => (
        <i
          key={star}
          className={`mdi ${star <= Math.round(value) ? 'mdi-star' : 'mdi-star-outline'}`}
          style={{ color }}
        ></i>
      ))}
      <span className="ms-1" style={{ color }}>
        ({value})
      </span>
    </span>
  );
};

const ServiceFeedbackList = ({ refreshList }) => {
  const dispatch = useDispatch();

  const [paginationValue, setPaginationValue] = useUrlSearchParams({ page: 0 });
  const [limitValue, setLimitValue] = useUrlSearchParams({ limit: 10 });
  const [searchParams, setSearchParams] = useUrlSearchParams({ search: '' });
  const [ratingParam, setRatingParam] = useUrlSearchParams({ rating: 'All' });

  const [searchValue, setSearchValue] = useState(searchParams.search || '');
  const [serviceType, setServiceType] = useState('');
  const [dateRangeValue, setDateRangeValue] = useState(null);
  const [dateFilter, setDateFilter] = useState({ frmDt: '', toDt: '' });
  const [isDataLoading, setDataLoading] = useState(false);
  const [listData, setListData] = useState([]);
  const [recordsTotal, setRecordsTotal] = useState(0);
  const [summary, setSummary] = useState(null);
  const [selectedFeedback, setSelectedFeedback] = useState(null);

  const currentCountry = localStorage.getItem('WEBSITE_COUNTRY');
  const ratingFilter = String(ratingParam?.rating ?? 'All');

  const loadList = useCallback(async (params) => {
    try {
      setDataLoading(true);
      const response = await fetchServiceFeedbackList({ params, dispatch });
      setListData(response?.listFeedback?.data || []);
      setRecordsTotal(Number(response?.listFeedback?.recordsTotal || 0));
      setSummary(response?.summary || null);
    } catch (error) {
      console.error('Error fetching service feedback:', error);
    } finally {
      setDataLoading(false);
    }
  }, []);

  useEffect(() => {
    const delay = setTimeout(() => {
      loadList({
        start: Number(paginationValue.page * limitValue.limit),
        limit: Number(limitValue.limit),
        search_qry: searchParams?.search || '',
        rating: ratingFilter === 'All' ? '' : ratingFilter,
        service_type: serviceType,
        country: currentCountry,
        advancedFilter: dateFilter,
      });
    }, 300);

    return () => clearTimeout(delay);
  }, [
    paginationValue.page,
    limitValue,
    searchParams,
    ratingFilter,
    serviceType,
    dateFilter,
    refreshList,
  ]);

  const handleDateChange = (dates) => {
    if (dates?.[0] && dates?.[1]) {
      setDateFilter({
        frmDt: moment(dates[0]).format('YYYY-MM-DD'),
        toDt: moment(dates[1]).format('YYYY-MM-DD'),
      });
      setDateRangeValue(dates);
      setPaginationValue({ page: 0 });
    }
  };

  const handleDateClose = () => {
    setDateFilter({ frmDt: '', toDt: '' });
    setDateRangeValue(null);
  };

  const handleSearchButtonClick = () => {
    setSearchParams({ search: searchValue });
    setPaginationValue({ page: 0 });
  };

  const handleResetSearch = () => {
    setSearchValue('');
    setSearchParams({ search: '' });
    setRatingParam({ rating: 'All' });
    setServiceType('');
    setPaginationValue({ page: 0 });
    handleDateClose();
  };

  const disableFutureDates = (date) => date.getTime() > new Date().getTime();

  return (
    <>
      <Row>
        <Col xl={12}>
          {summary && summary.total > 0 && (
            <Row className="mb-3">
              <Col md={12}>
                <div className="p-3 bg-white border rounded d-flex flex-wrap align-items-center gap-3">
                  <div>
                    <span className="fs-11 text-muted d-block">Average Rating</span>
                    <span
                      className="fw-semibold fs-5"
                      style={{ color: getRatingColor(summary.average) }}
                    >
                      {summary.average} / 5
                    </span>
                  </div>
                  <div>
                    <span className="fs-11 text-muted d-block">Total Feedback</span>
                    <span className="fw-semibold fs-5">{summary.total}</span>
                  </div>
                  <div className="d-flex gap-2 flex-wrap ms-auto">
                    {[5, 4, 3, 2, 1].map((star) => (
                      <span
                        key={star}
                        className="badge bg-soft-light text-dark border"
                        title={`${summary.ratings?.[star] || 0} rated ${star}`}
                      >
                        {star}
                        <i className="mdi mdi-star" style={{ color: getRatingColor(star) }}></i>{' '}
                        {summary.ratings?.[star] || 0}
                      </span>
                    ))}
                  </div>
                </div>
              </Col>
            </Row>
          )}

          <div
            className="p-3 bg-soft-light border-1 border-light"
            style={{ borderStyle: 'dashed' }}
          >
            <Row>
              <Col
                md={12}
                className="d-flex flex-row flex-wrap align-items-center gap-2"
              >
                <div style={{ minWidth: '240px' }}>
                  <Input
                    type="text"
                    placeholder="Search Job No / Customer / Technician"
                    value={searchValue}
                    onChange={(e) => setSearchValue(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleSearchButtonClick();
                    }}
                  />
                </div>
                <div style={{ minWidth: '150px' }}>
                  <select
                    className="form-control form-select"
                    value={ratingFilter}
                    onChange={(e) => {
                      setRatingParam({ rating: e.target.value });
                      setPaginationValue({ page: 0 });
                    }}
                  >
                    <option value="All">All Ratings</option>
                    {[5, 4, 3, 2, 1].map((star) => (
                      <option key={star} value={String(star)}>
                        {star} Star{star > 1 ? 's' : ''}
                      </option>
                    ))}
                  </select>
                </div>
                <div style={{ minWidth: '170px' }}>
                  <select
                    className="form-control form-select text-capitalize"
                    value={serviceType}
                    onChange={(e) => {
                      setServiceType(e.target.value);
                      setPaginationValue({ page: 0 });
                    }}
                  >
                    <option value="">All Service Types</option>
                    {SERVICE_TYPE_OPTIONS.map((opt) => (
                      <option key={opt} value={opt} className="text-capitalize">
                        {opt.replace(/_/g, ' ')}
                      </option>
                    ))}
                  </select>
                </div>
                <div style={{ minWidth: '240px' }}>
                  <DateRangePicker
                    size="md"
                    className="w-100"
                    placement="auto"
                    placeholder="Feedback Date Range"
                    showOneCalendar={false}
                    disabledDate={disableFutureDates}
                    onChange={handleDateChange}
                    onClean={handleDateClose}
                    value={dateRangeValue}
                  />
                </div>
                <div className="d-flex gap-2 flex-shrink-0">
                  <Button color="secondary" type="button" onClick={handleSearchButtonClick}>
                    <i className="bx bx-search-alt font-size-16 align-middle"></i>
                  </Button>
                  <Button outline color="danger" type="button" onClick={handleResetSearch}>
                    <i className="bx bx-revision font-size-16 align-middle"></i>
                  </Button>
                </div>
              </Col>
            </Row>
          </div>

          <div className="dt-table">
            <div className="table-responsive">
              <Table className="OrderListTable ServiceOrderTable table mb-0">
                <thead>
                  <tr>
                    <th nowrap="nowrap">Job No</th>
                    <th nowrap="nowrap">Rating</th>
                    <th nowrap="nowrap">Customer</th>
                    <th nowrap="nowrap">Technician</th>
                    <th nowrap="nowrap">Service Type</th>
                    <th nowrap="nowrap">Comment</th>
                    <th nowrap="nowrap">Submitted On</th>
                    <th nowrap="nowrap">Action</th>
                  </tr>
                </thead>
                {isDataLoading ? (
                  <tbody>
                    <tr>
                      <td colSpan={8} className="text-center">
                        <Spinner className="ms-2" color="primary" />
                      </td>
                    </tr>
                  </tbody>
                ) : listData?.length > 0 ? (
                  <tbody>
                    {listData.map((row) => (
                      <tr key={row.id}>
                        <td>
                          <p className="fs-12 mb-0">
                            <b
                              style={{ cursor: 'pointer', color: '#0d6efd' }}
                              title="View Service Order"
                              onClick={() => {
                                if (row?.complaint_no) {
                                  window.open(
                                    `/service-orders/manage-service-orders/${row.complaint_no}`,
                                    '_blank',
                                  );
                                }
                              }}
                            >
                              {row?.complaint_no || row?.reference_no}
                            </b>
                          </p>
                          {row?.service_order_no && (
                            <p className="fs-10 mb-0 mt-1">
                              <b>Service Order No :</b> {row.service_order_no}
                            </p>
                          )}
                        </td>
                        <td nowrap="nowrap">{renderStars(row?.rating)}</td>
                        <td>
                          <p className="fs-12 mb-0">{row?.customer_name || '-'}</p>
                          {row?.mobile && (
                            <p className="fs-10 mb-0 mt-1">{row.mobile}</p>
                          )}
                        </td>
                        <td>
                          <p className="fs-12 mb-0">{row?.technician_name || '-'}</p>
                          {row?.assigned_to && (
                            <p className="fs-10 mb-0 mt-1">{row.assigned_to}</p>
                          )}
                        </td>
                        <td className="text-capitalize" nowrap="nowrap">
                          {String(row?.service_type || '-').replace(/_/g, ' ')}
                        </td>
                        <td>
                          <span
                            className="fs-12 d-inline-block text-truncate"
                            style={{ maxWidth: '260px' }}
                            title={row?.comment || ''}
                          >
                            {row?.comment || '-'}
                          </span>
                        </td>
                        <td nowrap="nowrap">
                          {row?.created_at
                            ? moment(row.created_at).format('DD MMM, YYYY')
                            : '-'}
                        </td>
                        <td nowrap="nowrap">
                          <Button
                            size="sm"
                            color="dark"
                            outline
                            title="View feedback details"
                            onClick={() => setSelectedFeedback(row)}
                          >
                            <i className="mdi mdi-eye-outline"></i>
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                ) : (
                  <tbody>
                    <tr>
                      <td colSpan={8}>No Records found</td>
                    </tr>
                  </tbody>
                )}
              </Table>
            </div>
            {listData?.length > 0 ? (
              <DHPagination
                totalPages={recordsTotal}
                currentPage={paginationValue.page}
                setPaginationValue={setPaginationValue}
                setLimitValue={setLimitValue}
              />
            ) : null}
          </div>
        </Col>
      </Row>

      <ServiceFeedbackDetailsOffcanvas
        isOpen={!!selectedFeedback}
        toggle={() => setSelectedFeedback(null)}
        feedback={selectedFeedback}
      />
    </>
  );
};

export default ServiceFeedbackList;
